import React from "react";
import { toast } from "sonner";
import { Check, Copy, Eye, FileDown } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { PainBanner, Pill } from "./PersonaUi";
import { AL, ER, OBS_F, RF, SHORT, aer } from "./riaFundsData";

export default function RiaFundJustify() {
  const f = RF.onepager.fund;
  const g = RF.grades[f];
  const meta = AL.grades[f] || {};
  const as = g.active_share * 100;
  const conv = (g.lead_exp + g.follow_exp) * 100;
  const top5 = RF.onepager.top.slice(0, 5).reduce((s, x) => s + x.w, 0);
  return (
    <div className="space-y-4" data-testid="persona-ria-justify">
      <PainBanner testid="persona-ria-justify-pain" persona="RIA · FUND JUSTIFICATION"
        pain={`My client asks why we pay ${ER[f].toFixed(2)}% for this fund when an S&P 500 index fund costs 0.03%.`}
        answer="a plain-English, compliance-safe one-pager on what the fee buys" />
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => toast("Mockup — copy-for-email not wired yet")} data-testid="ria-justify-copy-btn"><Copy className="w-4 h-4 mr-1" />Copy for email</Button>
        <Button size="sm" onClick={() => toast("Mockup — branded PDF not wired yet")} data-testid="ria-justify-export-btn"><FileDown className="w-4 h-4 mr-1" />Export branded PDF</Button>
      </div>
      <Card className="max-w-4xl mx-auto bg-zinc-950" data-testid="ria-justify-sheet">
        <CardContent className="p-8 space-y-6">
          <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Why we own it · client brief</div>
              <div className="text-2xl font-bold mt-1">{SHORT[f]} ({f})</div>
              <div className="text-sm text-muted-foreground">Large-cap {meta.style || ""} mutual fund · {RF.onepager.n_positions} stocks</div>
            </div>
            <div className="text-right"><Pill tone="good">POSITIONING GRADE {g.letter}</Pill><div className="text-[10px] text-muted-foreground mt-1">{g.quintile === 5 ? "top quintile (top 20%)" : `quintile ${g.quintile}`} of large-cap peers · {OBS_F}</div></div>
          </div>
          <p className="text-lg leading-relaxed" data-testid="ria-justify-summary">
            This fund earns its fee by being genuinely different from the index — <b>{as.toFixed(0)}% of the portfolio differs from its benchmark</b> — and by owning, early,
            the companies that the most skilled sector specialists are now buying together.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ria-justify-pay-get">
            <div className="rounded border border-border/70 p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">What you pay</div>
              <div className="text-2xl font-mono font-bold mt-1">{ER[f].toFixed(2)}%</div>
              <div className="text-[12px] text-muted-foreground">≈ {aer(g).toFixed(2)}% for the active part alone</div></div>
            <div className="rounded border border-border/70 p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">How different from the index</div>
              <div className="text-2xl font-mono font-bold mt-1">{as.toFixed(1)}%</div>
              <div className="text-[12px] text-muted-foreground">active share (≥ 80% = truly active)</div></div>
            <div className="rounded border border-border/70 p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Where the experts are buying</div>
              <div className="text-2xl font-mono font-bold mt-1">{conv.toFixed(0)}%</div>
              <div className="text-[12px] text-muted-foreground">of the fund in those names — {Math.round(g.lead_exp * 100)} pts owned before the experts arrived</div></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm font-bold flex items-center gap-2 mb-2"><Check className="w-4 h-4 text-emerald-400" />Largest holdings</div>
              <div className="space-y-1 text-[13px]" data-testid="ria-justify-top">
                {RF.onepager.top.map((h) => (
                  <div key={h.symbol} className="flex justify-between"><span><span className="font-mono font-bold">{h.symbol}</span> <span className="text-muted-foreground">{h.name}</span></span><span className="font-mono">{h.w.toFixed(1)}%</span></div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-sm font-bold mb-1">Risks to understand</div>
                <ul className="text-[13px] text-foreground/80 list-disc pl-5 space-y-1" data-testid="ria-justify-risks">
                  <li>Concentrated: the top 5 holdings are {top5.toFixed(0)}% of the fund.</li>
                  <li>Growth style: it will lag when value stocks lead.</li>
                  <li>Active share is {as.toFixed(1)}% — only just above the 80% line we use to call a fund truly active.</li>
                </ul>
              </div>
              <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3" data-testid="ria-justify-revisit">
                <div className="text-sm font-bold flex items-center gap-2"><Eye className="w-4 h-4 text-amber-400" />What would make us revisit</div>
                <div className="text-[13px] text-foreground/80 mt-1">Active share drops below 80% · positioning grade falls to D or F · crowded holdings rise above 12% (today {((g.crowded_share || 0) * 100).toFixed(1)}%).</div>
              </div>
            </div>
          </div>
          <div className="text-[10px] text-muted-foreground border-t border-border pt-3">
            Based on the fund's SEC filings (N-PORT) and Kensys positioning research. The positioning grade compares the fund's weight in names where at least three elite sector
            specialists converge; historically top-third large-cap funds beat bottom-third peers by about 3.3% over six months. Fee is an approximate public figure. Past results do not
            guarantee future results. Not investment advice. Mockup.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
